// Cleanups.tsx — what docdaemon thinks is wrong with a directory.
//
// The documentation says what the code is for. This says what it should
// probably have been instead, which is a different kind of claim and is shown
// as one: proposals, ranked, each with the reason it is worth an afternoon and
// what to actually do. Nothing here is committed back to the repository — a
// description is something docdaemon is confident about and a cleanup is an
// opinion, and putting both in git history at the same weight would devalue
// the first.
//
// The finding worth running this for is drift: the recorded page describes
// behaviour the source no longer has. Nothing else on the estate can find that,
// because it needs both halves and only docdaemon holds both.
//
// Proposing is admin-gated and re-review replaces rather than accumulates. Two
// reports on one directory from different revisions are not a history, they are
// a contradiction — so the panel shows one opinion and says which commit it was
// formed at.
//
// An administrator answers each finding one of two ways: File issue (worth
// doing) or Dismiss (not). Both are remembered across a re-review, so a
// dismissed finding does not come back on the next push. Dismiss asks for an
// optional reason: the next review is shown it, which is what stops the same
// point coming back under a different title.
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import { BsBoxArrowUpRight, BsBug, BsExclamationCircle, BsStars, BsXLg } from 'react-icons/bs';
import { StatusBadge } from 'common';
import type { StatusTone } from 'common';
import DismissDialog from './DismissDialog';
import { dismissCleanup, fetchCleanups, fileCleanupIssue, proposeCleanups } from '../api';
import type { CleanupFinding, CleanupSeverity, DocNode } from '../api';
import { relativeTime, shortSha } from '../format';

// A total map, so a severity the server starts emitting fails the typecheck
// here rather than rendering as an unstyled word.
const SEVERITY_TONE: Record<CleanupSeverity, StatusTone> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

export default function Cleanups({ owner, name, dir, isAdmin, onSelectPath }: {
  owner: string;
  name: string;
  // The directory under review. '' is the repository root.
  dir: string;
  isAdmin: boolean;
  // Findings name files; clicking one should take the reader there.
  onSelectPath?: (path: string) => void;
}) {
  const [findings, setFindings] = useState<CleanupFinding[] | null>(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  // Which finding is mid-flight, and what went wrong with which one. Both are
  // keyed by finding id rather than being a single flag: the buttons sit in a
  // list, and a spinner or an error that appeared on all of them would say the
  // wrong thing about every row but one.
  const [pending, setPending] = useState<{ id: string; action: RowAction } | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  // The finding whose Dismiss dialog is open, or null.
  const [asking, setAsking] = useState<CleanupFinding | null>(null);

  useEffect(() => {
    let live = true;
    setFindings(null);
    setError('');
    setRowErrors({});
    void (async () => {
      try {
        const list = await fetchCleanups(owner, name, dir);
        if (live) setFindings(list);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Failed to load findings');
      }
    })();
    return () => { live = false; };
  }, [owner, name, dir]);

  const review = async () => {
    setRunning(true);
    setError('');
    setRowErrors({});
    try {
      setFindings(await proposeCleanups(owner, name, dir));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review the directory');
    } finally {
      setRunning(false);
    }
  };

  // act answers one finding. Filing opens a Gitea issue and swaps that row
  // for the one the server returns, which now carries the issue link. Only
  // that row is replaced: re-fetching the list would discard nothing useful
  // but would make a single button press look like the whole panel reloaded.
  //
  // dismiss sets one finding aside and drops its row: the list the server
  // returns never includes a dismissed finding, so keeping it on screen would
  // show something a reload would not. The row's Dismiss button only opens
  // DismissDialog to ask why; this runs once that dialog commits, with the
  // reason or ''.
  const act = async (id: string, action: RowAction, reason = '') => {
    setPending({ id, action });
    setRowErrors((prev) => {
      const { [id]: _dropped, ...rest } = prev;
      return rest;
    });
    try {
      if (action === 'file') {
        const filed = await fileCleanupIssue(owner, name, id);
        setFindings((prev) => prev?.map((f) => (f.id === id ? filed : f)) ?? prev);
      } else {
        await dismissCleanup(owner, name, id, reason);
        setFindings((prev) => prev?.filter((f) => f.id !== id) ?? prev);
      }
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [id]: err instanceof Error
          ? err.message
          : action === 'file' ? 'Failed to open the issue' : 'Failed to dismiss the finding',
      }));
    } finally {
      setPending(null);
    }
  };

  const reviewedAt = findings?.[0]?.reviewed_sha ?? '';
  const filedCount = findings?.filter((f) => f.issue_number > 0).length ?? 0;

  return (
    <>
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between gap-2">
          <span className="text-truncate">Proposed Cleanups</span>
          {isAdmin && (
            <Button
              variant="outline-primary"
              size="sm"
              className="flex-shrink-0"
              onClick={() => void review()}
              disabled={running}
              title="Read this directory's pages and source, and propose changes"
            >
              <span className="d-inline-flex align-items-center gap-2">
                {running ? <Spinner animation="border" size="sm" /> : <BsStars />}
                {running ? 'Reviewing…' : findings && findings.length > 0 ? 'Review again' : 'Review'}
              </span>
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          {!findings ? (
            <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : findings.length === 0 ? (
            <p className="text-muted small m-0">
              {isAdmin
                ? 'Nothing proposed for this directory yet. A review reads its pages and its source together — which is what makes drift between the two findable.'
                : 'Nothing proposed for this directory yet.'}
            </p>
          ) : (
            <ul className="cleanup-list">
              {findings.map((f) => (
                <li key={f.id} className="cleanup-item">
                  <div className="d-flex align-items-baseline gap-2 flex-wrap">
                    <StatusBadge tone={SEVERITY_TONE[f.severity]} uppercase={false}>
                      {f.severity}
                    </StatusBadge>
                    {f.kind && <span className="small text-muted">{f.kind}</span>}
                    {onSelectPath ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        onClick={() => onSelectPath(f.path)}
                      >
                        <code>{f.path}</code>
                      </Button>
                    ) : <code>{f.path}</code>}
                    {/* The issue control sits at the end of the identifying row
                        rather than under the prose, so a list of findings can be
                        scanned for what has already been picked up without
                        reading any of them. */}
                    <span className="ms-auto flex-shrink-0">
                      <IssueControl
                        finding={f}
                        isAdmin={isAdmin}
                        pending={pending?.id === f.id ? pending.action : null}
                        // One at a time. Two requests at once is fine for the
                        // server, but a reader who double-clicked would have no
                        // way to tell which spinner was theirs.
                        disabled={pending !== null}
                        onFile={() => void act(f.id, 'file')}
                        onDismiss={() => setAsking(f)}
                      />
                    </span>
                  </div>
                  <p className="cleanup-title">{f.title}</p>
                  {f.rationale && <p className="small text-muted mb-1">{f.rationale}</p>}
                  {f.suggestion && <p className="small mb-0"><strong>Do:</strong> {f.suggestion}</p>}
                  {rowErrors[f.id] && (
                    <p className="small text-danger mt-1 mb-0 d-flex align-items-baseline gap-1">
                      <BsExclamationCircle className="flex-shrink-0" />
                      <span>{rowErrors[f.id]}</span>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
        <Card.Footer className="small text-muted">
          {/* Which commit the opinion was formed at, so a report the branch has
              moved past says so instead of looking current. */}
          {reviewedAt
            ? <>Reviewed at <code>{shortSha(reviewedAt)}</code>
              {findings && findings.length > 0 && <> · {relativeTime(findings[0].created_at)}</>}
              {filedCount > 0 && <> · {filedCount} filed as {filedCount === 1 ? 'an issue' : 'issues'}</>}</>
            : 'Nothing has been reviewed here yet.'}
        </Card.Footer>
      </Card>
      <DismissDialog
        title={asking?.title ?? ''}
        show={asking !== null}
        onCancel={() => setAsking(null)}
        onDismiss={(reason) => {
          const id = asking?.id;
          setAsking(null);
          if (id) void act(id, 'dismiss', reason);
        }}
      />
    </>
  );
}

// RowAction is what an administrator can do to one finding.
type RowAction = 'file' | 'dismiss';

// IssueControl is one finding's issue state: the link once it has one, the
// buttons to file or dismiss it, or nothing.
//
// Three states rather than a disabled button, because "already filed" is not a
// weaker version of "can file" — it is the more useful of the two, and the
// number is what a reader wants next. A non-administrator sees the link and no
// button, which is the honest rendering of what they can do: they can read
// that this was picked up, and they cannot pick it up themselves.
//
// Dismiss sits beside File issue and only there: a filed finding's decision
// lives in its issue now, and the server refuses to hide it.
function IssueControl({ finding, isAdmin, pending, disabled, onFile, onDismiss }: {
  finding: CleanupFinding;
  isAdmin: boolean;
  pending: RowAction | null;
  disabled: boolean;
  onFile: () => void;
  onDismiss: () => void;
}) {
  if (finding.issue_number > 0) {
    return (
      <a
        href={finding.issue_url}
        target="_blank"
        rel="noreferrer"
        className="small text-decoration-none d-inline-flex align-items-center gap-1"
        title={`Filed as issue #${finding.issue_number}${
          finding.issue_filed_at ? ` ${relativeTime(finding.issue_filed_at)}` : ''
        }`}
      >
        #{finding.issue_number}
        <BsBoxArrowUpRight />
      </a>
    );
  }
  if (!isAdmin) return null;
  const filing = pending === 'file';
  const dismissing = pending === 'dismiss';
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <Button
        variant="outline-secondary"
        size="sm"
        className="py-0"
        onClick={onFile}
        disabled={disabled}
        title="Open an issue on this repository for this finding, labelled for-ai"
      >
        <span className="d-inline-flex align-items-center gap-1">
          {filing ? <Spinner animation="border" size="sm" /> : <BsBug />}
          {filing ? 'Filing…' : 'File issue'}
        </span>
      </Button>
      <Button
        variant="outline-secondary"
        size="sm"
        className="py-0"
        onClick={onDismiss}
        disabled={disabled}
        title="Not worth doing: hide this finding, and keep it hidden when the directory is reviewed again"
      >
        <span className="d-inline-flex align-items-center gap-1">
          {dismissing ? <Spinner animation="border" size="sm" /> : <BsXLg />}
          {dismissing ? 'Dismissing…' : 'Dismiss'}
        </span>
      </Button>
    </span>
  );
}

// selectByPath finds the tree node a finding names, so clicking one navigates
// instead of only reading as a path.
export function findNode(root: DocNode | null, path: string): DocNode | null {
  if (!root) return null;
  if (root.path === path) return root;
  for (const child of root.children ?? []) {
    const found = findNode(child, path);
    if (found) return found;
  }
  return null;
}
